<script lang="ts" setup>
import { useIntervalFn } from '@vueuse/core';
import { useAuthStore } from '~/store/auth';
import { FeatureKey } from '~/models/feature';
import { systemAnnouncementService } from '~/services/system-announcement.service';
import { useSystemAnnouncements } from '~/composables/useSystemAnnouncements';
import { usePageHeading } from '~/libs/admin-web-common/composables/usePageHeading';
import { useChatPanelStore } from '~/store/chatPanel';
import { chatShortcutLabel } from '~/utils/chat-panel';

const { t } = useI18n();
const { heading: pageHeading } = usePageHeading();

const chatVisible = useChatAssistantVisible();
const chatPanel = useChatPanelStore();
const platform = navigator.platform;
const showChangePasswordDialog = ref(false);

const data = ref({
  title: 'POS Admin',
  drawer: true,
  loading: false,
});

const { logout, changeCurrentShopId, changeCurrentOrganizationId, canUseFeature, shopHasFeature, canOpenPage } =
  useAuthStore();
const { currentShop, currentOrganization, shops, organizations, operator } = storeToRefs(useAuthStore());

// Poll for new announcements on load and every 30 minutes; show red dot when unseen items exist.
const { hasUnseen } = useSystemAnnouncements();
const latestAnnouncementCreatedAt = ref<string | null>(null);
const hasNewAnnouncement = computed(() => hasUnseen(latestAnnouncementCreatedAt.value));

const pollLatestAnnouncement = async () => {
  const { result } = await systemAnnouncementService.latest();
  if (result?.data) {
    latestAnnouncementCreatedAt.value = result.data.latest_created_at;
  }
};

onMounted(pollLatestAnnouncement);
useIntervalFn(pollLatestAnnouncement, 30 * 60 * 1000);

// Check if app is in production
const config = useRuntimeConfig();
const isProduction = computed(() => config.public.appEnv === 'production');

const currentShopId = ref<number | undefined>(currentShop.value?.id);
const currentOrganizationId = ref<number | undefined>(currentOrganization.value?.id);

watch(currentShopId, (newId: number | undefined) => {
  const nextId = newId != null ? Number(newId) : undefined;
  if (!nextId || !operator.value) return;
  // v-select may emit string ids; skip no-op so we don't reload unnecessarily
  if (nextId === Number(currentShop.value?.id)) return;
  changeCurrentShopId(nextId);
});

watch(currentOrganizationId, (newId: number | undefined) => {
  const nextId = newId != null ? Number(newId) : undefined;
  if (!nextId || nextId === Number(currentOrganization.value?.id)) return;
  changeCurrentOrganizationId(nextId);
});

// The search text starts as the current name. Select it on focus so that typing replaces it.
const selectSearchText = (event: FocusEvent) => {
  if (event.target instanceof HTMLInputElement) event.target.select();
};

// Clearing the search text clears the model, but the header always has a current
// organization and shop. When focus leaves without a new pick, show the current one again.
const restoreSelection = () => {
  currentOrganizationId.value ??= currentOrganization.value?.id;
  currentShopId.value ??= currentShop.value?.id;
};

interface SidebarItem {
  header?: string;
  to?: string;
  displayable: boolean;
  lists?: SidebarItem[];
  [key: string]: unknown;
}

/**
 * Hides each link that the operator cannot open (see utils/page-permission.ts), then
 * each group and header that has no visible link left.
 */
const applyPagePermissions = (items: SidebarItem[]): SidebarItem[] => {
  const withLinks = items.map(item => {
    if (item.lists) {
      const lists = item.lists.map(link => ({
        ...link,
        displayable: link.displayable && (!link.to || canOpenPage(link.to)),
      }));
      return { ...item, lists, displayable: item.displayable && lists.some(link => link.displayable) };
    }
    return { ...item, displayable: item.displayable && (!item.to || canOpenPage(item.to)) };
  });

  return withLinks.map((item, index) => {
    if (!item.header) return item;
    const nextHeader = withLinks.findIndex((other, otherIndex) => otherIndex > index && other.header);
    const section = withLinks.slice(index + 1, nextHeader === -1 ? undefined : nextHeader);
    return { ...item, displayable: item.displayable && section.some(other => other.displayable) };
  });
};

const sidebarItems = computed(() =>
  applyPagePermissions([
    {
      header: t('side_bar.system'),
      displayable: true,
    },
    {
      icon: 'mdi-cash-multiple',
      title: t('side_bar.system_dashboard'),
      to: '/system-dashboard',
      displayable: true,
    },
    {
      icon: 'mdi-domain',
      title: t('models.organization'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-domain',
          title: t('models.organization'),
          to: '/organizations',
          displayable: true,
        },
        {
          icon: 'mdi-server-outline',
          title: t('models.local_server'),
          to: '/local-servers',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-blur-linear',
      title: t('side_bar.configuration'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-file-cog-outline',
          title: t('side_bar.app_configuration'),
          to: '/app-configurations',
          displayable: true,
        },
        {
          icon: 'mdi-card-text-outline',
          title: t('pages.system_properties.index.title'),
          to: '/system-properties',
          displayable: true,
        },
        {
          icon: 'mdi-ip-network',
          title: t('models.ip_whitelist'),
          to: '/ip-whitelists',
          displayable: true,
        },
        {
          icon: 'mdi-key-variant',
          title: t('models.external_authorization'),
          to: '/external-authorizations',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-bell-outline',
      title: t('side_bar.announcements'),
      to: '/announcements',
      displayable: true,
      badge: hasNewAnnouncement.value,
    },
    {
      icon: 'mdi-headset',
      title: t('side_bar.support'),
      to: '/support',
      displayable: true,
    },
    {
      header: t('side_bar.report'),
      displayable: true,
    },
    {
      icon: 'mdi-view-dashboard-outline',
      title: t('side_bar.report_dashboard'),
      to: '/report-dashboard',
      displayable: true,
    },
    {
      icon: 'mdi-trending-up',
      title: t('side_bar.report_trend'),
      to: '/report-trend',
      displayable: true,
    },
    {
      icon: 'mdi-calendar-today',
      title: t('side_bar.report_daily'),
      to: '/report-daily',
      displayable: true,
    },
    {
      icon: 'mdi-finance',
      title: t('side_bar.analysis'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-chart-line',
          title: t('side_bar.report_sales_general'),
          to: '/report-sales',
          displayable: true,
        },
        {
          icon: 'mdi-clock-time-four-outline',
          title: t('side_bar.report_sales_dayparting'),
          to: '/report-dayparting',
          displayable: true,
        },
        {
          icon: 'mdi-tag-outline',
          title: t('side_bar.report_sales_category'),
          to: '/report-categories',
          displayable: true,
        },
        {
          icon: 'mdi-food-variant',
          title: t('side_bar.report_sales_menu_items'),
          to: '/report-menu-items',
          displayable: true,
        },
        {
          icon: 'mdi-file-document-remove',
          title: t('side_bar.report_void'),
          to: '/report-void',
          displayable: true,
        },
        {
          icon: 'mdi-account-voice',
          title: t('side_bar.report_operators'),
          to: '/report-operators',
          displayable: true,
        },
        {
          icon: 'mdi-network-pos',
          title: t('side_bar.report_payments'),
          to: '/report-payments',
          displayable: true,
        },
        {
          icon: 'mdi-account-group-outline',
          title: t('side_bar.report_customers'),
          to: '/report-customers',
          displayable: true,
        },
        {
          icon: 'mdi-chart-box',
          title: t('side_bar.general_report'),
          to: '/report-general',
          displayable: true,
        },
      ],
    },
    {
      header: t('side_bar.transactions'),
      displayable: true,
    },
    {
      icon: 'mdi-domain',
      title: t('side_bar.hms'),
      displayable:
        shopHasFeature(currentShop.value?.uid || '', FeatureKey.Hms) ||
        shopHasFeature(currentShop.value?.uid || '', FeatureKey.HmsImportReceipt),
      lists: [
        {
          icon: 'mdi-credit-card-check-outline',
          title: t('side_bar.hms_receipt_payments'),
          to: '/hms-receipt-payments',
          displayable: shopHasFeature(currentShop.value?.uid || '', FeatureKey.Hms),
        },
        {
          icon: 'mdi-door',
          title: t('side_bar.hms_rooms'),
          to: '/hms-rooms',
          displayable: shopHasFeature(currentShop.value?.uid || '', FeatureKey.HmsImportReceipt),
        },
      ],
    },
    {
      icon: 'mdi-receipt-text-check',
      title: t('models.receipt'),
      to: '/receipts',
      displayable: true,
    },
    {
      icon: 'mdi-file-document-outline',
      title: t('models.invoice'),
      to: '/invoices',
      displayable: true,
    },
    {
      header: t('side_bar.organization_management'),
      displayable: true,
    },
    {
      icon: 'mdi-storefront',
      title: t('models.shop'),
      to: '/shops',
      displayable: true,
    },
    {
      icon: 'mdi-account',
      title: t('models.operator'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-account',
          title: t('models.operator'),
          to: '/operators',
          displayable: true,
        },
        {
          icon: 'mdi-shield-account-outline',
          title: t('models.admin_role'),
          to: '/admin-roles',
          displayable: true,
        },
        {
          icon: 'mdi-security',
          title: t('models.role'),
          to: '/roles',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-blur-linear',
      title: t('side_bar.system'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-calendar-text',
          title: t('models.admin_audit_log'),
          to: '/admin-audit-logs',
          displayable: true,
        },
        {
          icon: 'mdi-link',
          title: t('side_bar.integrations'),
          to: `/integrations`,
          displayable: canUseFeature(FeatureKey.SquarePayment) || canUseFeature(FeatureKey.Hms),
        },
        {
          icon: 'mdi-lan',
          title: t('routers.network-topology'),
          to: '/network-topology',
          displayable: true,
        },
      ],
    },
    {
      header: t('side_bar.general_settings'),
      displayable: true,
    },
    {
      icon: 'mdi-store',
      title: t('side_bar.shop_settings'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-storefront-outline',
          title: t('models.outlet'),
          to: '/outlets',
          displayable: canUseFeature(FeatureKey.Outlet),
        },
        {
          icon: 'mdi-home-city-outline',
          title: t('models.revenue_center'),
          to: '/revenue-centers',
          displayable: true,
        },
        {
          icon: 'mdi-floor-plan',
          title: t('models.area'),
          to: '/areas',
          displayable: true,
        },
        {
          icon: 'mdi-toggle-switch-outline',
          title: t('models.mode'),
          to: '/modes',
          displayable: true,
        },
        {
          icon: 'mdi-clock-time-four-outline',
          title: t('models.dayparting'),
          to: '/daypartings',
          displayable: true,
        },
        {
          icon: 'mdi-printer-outline',
          title: t('models.printer'),
          to: '/printers',
          displayable: true,
        },
        {
          icon: 'mdi-card-text-outline',
          title: t('models.property'),
          to: '/properties',
          displayable: !!currentShop.value?.shop_properties?.length,
        },
        {
          icon: 'mdi-wallet-outline',
          title: t('models.budget'),
          to: '/budgets',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-food',
      title: t('side_bar.menu_management'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-silverware-fork-knife',
          title: t('models.menu_item'),
          to: '/menu-items',
          displayable: true,
        },
        {
          icon: 'mdi-shaker-outline',
          title: t('models.condiment'),
          to: '/condiments',
          displayable: true,
        },
        {
          icon: 'mdi-silverware',
          title: t('models.course'),
          to: '/courses',
          displayable: true,
        },
        {
          icon: 'mdi-tag-outline',
          title: t('models.category'),
          to: '/categories',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-coffee',
      title: t('side_bar.charge_management'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-cash-check',
          title: t('models.order_charge'),
          to: '/order-charges',
          displayable: true,
        },
        {
          icon: 'mdi-table-furniture',
          title: t('models.table_charge'),
          to: '/table-charges',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-cash-multiple',
      title: t('side_bar.payment_management'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-credit-card-outline',
          title: t('models.payment'),
          to: '/payments',
          displayable: true,
        },
        {
          icon: 'mdi-ticket-percent',
          title: t('models.coupon'),
          to: '/coupons',
          displayable: true,
        },
        {
          icon: 'mdi-sale',
          title: t('models.discount'),
          to: '/discounts',
          displayable: true,
        },
      ],
    },
    {
      header: t('side_bar.pos_settings'),
      displayable: true,
    },
    {
      icon: 'mdi-tablet-cellphone',
      title: t('side_bar.app_settings'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-tablet',
          title: t('models.device'),
          to: '/devices',
          displayable: true,
        },
        {
          icon: 'mdi-map-outline',
          title: t('models.table_map'),
          to: '/table-maps',
          displayable: true,
        },
        {
          icon: 'mdi-keyboard-outline',
          title: t('side_bar.key_arrangement'),
          to: '/key-arrangements',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-tablet-dashboard',
      title: t('side_bar.layout_management'),
      displayable: true,
      lists: [
        {
          icon: 'mdi-menu-open',
          title: t('models.order_layout'),
          to: '/order-layouts',
          displayable: true,
        },
        {
          icon: 'mdi-menu-open',
          title: t('models.order_group'),
          to: '/order-groups',
          displayable: true,
        },
      ],
    },
    {
      header: t('side_bar.table_order_management'),
      displayable: canUseFeature(FeatureKey.TableOrder),
    },
    {
      icon: 'mdi-cellphone-text',
      title: t('side_bar.app_settings'),
      displayable: canUseFeature(FeatureKey.TableOrder),
      lists: [
        {
          icon: 'mdi-eye-outline',
          title: t('pages.table_order_view_settings.index.side_bar_title'),
          to: '/table-order-view-settings',
          displayable: true,
        },
        {
          icon: 'mdi-chair-rolling',
          title: t('models.table_order_table_configuration'),
          to: '/table-order-table-configurations',
          displayable: true,
        },
      ],
    },
    {
      icon: 'mdi-group',
      title: t('side_bar.layout_management'),
      displayable: canUseFeature(FeatureKey.TableOrder),
      lists: [
        {
          icon: 'mdi-layers-outline',
          title: t('models.table_order_menu_group'),
          to: '/table-order-menu-groups',
          displayable: true,
        },
        {
          icon: 'mdi-layers-triple-outline',
          title: t('models.table_order_menu_subgroup'),
          to: '/table-order-menu-subgroups',
          displayable: true,
        },
        {
          icon: 'mdi-plus-circle-outline',
          title: t('models.table_order_topping_group'),
          to: '/table-order-topping-groups',
          displayable: true,
        },
      ],
    },
  ]),
);

// Operator initials for avatar
const operatorInitials = computed(() => {
  const name = operator.value?.name || '';
  return name
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
});
</script>

<template>
  <!-- v-navigation-drawer registers BEFORE v-app-bar so the drawer claims the
       full viewport height and the app bar is offset to start at left: 256px. -->
  <admin-sidebar
    v-model:drawer="data.drawer"
    :items="sidebarItems"
    help-docs-base-path="/help"
  />

  <v-app-bar
    class="m-app-bar bg-white"
    height="64"
    flat
  >
    <div class="app-bar-heading">
      <!-- Render from the same pageHeading ref as Help (not a child component)
           so title and help stay in sync with useState. -->
      <div
        v-if="pageHeading?.title"
        class="page-heading"
      >
        <h1 class="page-heading__title">{{ pageHeading.title }}</h1>
      </div>
      <help-button
        v-if="pageHeading?.helpPath"
        :path="pageHeading.helpPath"
        compact
      />
    </div>

    <v-spacer />

    <template #append>
      <v-chip
        v-if="!isProduction"
        color="error"
        size="small"
        class="mr-4"
        variant="tonal"
        label
      >
        {{ config.public.appEnv.toUpperCase() }}
      </v-chip>
      <!-- Organization selector (system managers only) -->
      <m-autocomplete
        v-if="isSystemManager(operator)"
        v-model="currentOrganizationId"
        :items="organizations"
        item-title="name"
        item-value="id"
        :inlineLabel="$t('models.organization')"
        class="header-selection mr-2"
        @focusin="selectSearchText"
        @focusout="restoreSelection"
      />

      <!-- Shop selector -->
      <m-autocomplete
        v-model="currentShopId"
        :items="shops"
        item-title="name"
        item-value="id"
        :inlineLabel="$t('models.shop')"
        class="header-selection mr-4"
        @focusin="selectSearchText"
        @focusout="restoreSelection"
      />

      <!-- User menu -->
      <v-menu
        offset-y
        left
      >
        <template #activator="{ props }">
          <div
            v-bind="props"
            class="d-flex align-center cursor-pointer user-menu pr-6"
          >
            <div class="user-avatar">
              {{ operatorInitials }}
            </div>
            <div class="d-inline-flex flex-column justify-center ml-2">
              <span class="user-name">{{ operator?.name }}</span>
              <small class="user-role">
                {{
                  operator?.admin_role
                    ? adminRoleName(operator.admin_role)
                    : t(`enums.operator_account_type.${operator?.account_type}`)
                }}
              </small>
            </div>
            <v-icon
              size="16"
              color="muted-text"
              class="ml-2"
            >
              mdi-chevron-down
            </v-icon>
          </div>
        </template>

        <v-list>
          <v-list-item
            link
            prepend-icon="mdi-lock-reset"
            :title="$t('buttons.change_password')"
            @click="showChangePasswordDialog = true"
          />
          <v-list-item
            link
            prepend-icon="mdi-logout-variant"
            :title="$t('buttons.logout')"
            @click="logout()"
          />
        </v-list>
      </v-menu>

      <!-- The chat assistant "Suge" stays at the far right of the header. The open panel takes the
           full window height beside the header and has its own close button, thus the header
           gives the button space back to the page title. -->
      <v-badge
        v-if="chatVisible && !chatPanel.open"
        :model-value="chatPanel.unread"
        color="error"
        dot
        offset-x="6"
        offset-y="6"
        class="chat-button mr-4"
      >
        <v-btn
          :variant="chatPanel.open ? 'flat' : 'tonal'"
          color="primary"
          icon
          :aria-pressed="chatPanel.open"
          :aria-label="t('chat.button')"
          :title="`${t('chat.button')} (${chatShortcutLabel(platform)})`"
          @click="chatPanel.toggle()"
        >
          <v-icon size="20">mdi-creation</v-icon>
        </v-btn>
      </v-badge>
    </template>
  </v-app-bar>

  <change-password-dialog v-model="showChangePasswordDialog" />
  <merci-confirm ref="confirm" />
  <resources-confirm ref="resourcesConfirm" />
</template>

<style lang="scss" scoped>
.app-bar-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  max-width: 100%;
  overflow: hidden;
}

.page-heading {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  max-width: 100%;
  padding-left: 16px;
  flex: 1 1 auto;

  &__title {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
    color: rgb(var(--v-theme-dark-900));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
  }

  &__parent {
    font-size: 12px;
    color: rgb(var(--v-theme-dark-500));
    white-space: nowrap;
    flex-shrink: 0;
  }
}

.header-selection {
  width: 250px;
  // The header is narrower while the chat panel is open. The selectors give way before the title.
  min-width: 160px;
  flex-shrink: 1;
}

:deep(.v-toolbar__append) {
  margin-inline-end: 0;
  min-width: 0;
  flex-shrink: 1;
}

.m-app-bar {
  border-bottom: 1px solid var(--color-border) !important;
  box-shadow: none !important;
  background-color: white !important;

  :deep(.v-toolbar__content),
  :deep(.v-toolbar__extension),
  :deep(.v-app-bar__content) {
    background-color: white !important;
  }

  // Override any Vuetify overlay or scrim effects on the app bar
  &::before,
  &::after {
    display: none !important;
  }
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgb(var(--v-theme-primary-50));
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: rgb(var(--v-theme-dark-900));
  line-height: 1.4;
}

.user-role {
  font-size: 12px;
  color: rgb(var(--v-theme-muted-text));
  line-height: 1.2;
}
</style>
